import {
  useAuth,
  useClerk,
  useUser as useClerkUser,
  useOrganizationList,
  useSession,
} from "@clerk/clerk-react";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useClient, useMutation, useQuery, useSubscription } from "urql";
import { getFragmentData, graphql } from "../../__generated__/gql";
import * as Sentry from "@sentry/react";
import * as amplitude from "@amplitude/analytics-browser";
import { useLocation, useSearchParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import UserContext, { UserContextType } from "./UserContext";
import defaultUserContext from "./default-user-context";
import useToken from "../TokenProvider/use-token";

const BillingInfoFragment = graphql(/* GraphQL */ `
  fragment BillingInfo on BillingInfo {
    credits
    creditsUsed
    freeCreditsIssued
    keywordQueryCredits
    keywordQueriesPerMonth
    keywordQueryCreditPrice
    billingTier
    articlesPerMonth
    downgradeBillingTier
    pricePerArticle
    imagesPerArticleLimit
    linkedTeamsLimit
    scrapeLimit
    subscriptionPrice
    currentPeriodStart
    currentPeriodEnd
    currentMonthStart
    currentMonthEnd
  }
`);

const SetupUserMutation = graphql(/* GraphQL */ `
  mutation setupUser {
    setupUser {
      id
    }
  }
`);

const MeQuery = graphql(/* GraphQL */ `
  query me {
    me {
      id
      firstName
      lastName
      identityId
      email
      intercomHash
    }
    billingInfo {
      ...BillingInfo
    }
    keywordQueryUsageForCurrentPeriod {
      keywordQueries
    }
    usageForCurrentPeriod {
      articles
    }
    tenant {
      id
      identityId
      name
      memberCount
      isOnboardingComplete
      createdAt
      parentTenant {
        id
        identityId
        name
      }
      linkedTenants {
        id
      }
    }
  }
`);

const UsageUpdatedSubscription = graphql(/* GraphQL */ `
  subscription usageUpdated {
    usageUpdated {
      articles
    }
  }
`);

const KeywordQueryUsageUpdatedSubscription = graphql(/* GraphQL */ `
  subscription keywordQueryUsageUpdated {
    keywordQueryUsageUpdated {
      keywordQueries
    }
  }
`);

const BillingInfoUpdatedSubscription = graphql(/* GraphQL */ `
  subscription billingInfoUpdated {
    billingInfoUpdated {
      ...BillingInfo
    }
  }
`);

function UserProvider({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();
  const { user, isLoaded } = useClerkUser();
  const { signOut: clerkSignOut, setActive } = useClerk();
  const { userMemberships, isLoaded: isOrganizationListLoaded } =
    useOrganizationList({
      userMemberships: {
        infinite: true,
      },
    });
  const { getToken, orgId } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const [isReady, setIsReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const { refreshToken } = useToken();
  const [, setupUser] = useMutation(SetupUserMutation);
  const client = useClient();
  const didInitialize = useRef(false);
  const { session } = useSession();
  const [meQueryResponse] = useQuery({ query: MeQuery, pause: !isReady });

  useSubscription({ query: UsageUpdatedSubscription });
  useSubscription({ query: KeywordQueryUsageUpdatedSubscription });
  useSubscription({ query: BillingInfoUpdatedSubscription });

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const signInOrg = searchParams.get("sign_in_with_org");

  const billingInfo = getFragmentData(
    BillingInfoFragment,
    meQueryResponse.data?.billingInfo,
  );

  const selectOrganization = useCallback(
    async (id: string) => {
      if (setActive) {
        if (meQueryResponse.data) {
          localStorage.setItem(
            "previousOrganizationId",
            meQueryResponse.data.tenant.identityId,
          );
        }

        await setActive({ organization: id });
        await user?.reload();
        await session?.touch();

        if (signInOrg) {
          setSearchParams();
        }

        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    },
    [
      setActive,
      meQueryResponse.data,
      user,
      session,
      signInOrg,
      setSearchParams,
    ],
  );

  useEffect(() => {
    const initialize = async ({
      orgId,
      retry = false,
    }: {
      orgId?: string;
      retry?: boolean;
    }) => {
      didInitialize.current = true;

      let selectedOrgId = orgId;

      // If the user does not already have an organization in clerk, we need to
      // set up the user.
      if (!selectedOrgId) {
        const response = await setupUser({});

        if (response.data?.setupUser) {
          const memberships = await user?.getOrganizationMemberships();

          const data = memberships?.data;

          if (data?.[0]) {
            selectedOrgId = data[0].organization.id;
          }
        }
      }

      if (selectedOrgId) {
        await selectOrganization(selectedOrgId);
      }

      await refreshToken();

      const response = await client.query(MeQuery, {});

      if (response.data?.me) {
        if (response.data.me.identityId) {
          amplitude.setUserId(response.data.me.identityId);
        }

        setIsReady(true);
        return;
      }

      if (response.error) {
        if (retry) {
          if (
            response.error.graphQLErrors.find(
              (e) => e.extensions.code === "TENANT_NOT_FOUND",
            )
          ) {
            await initialize({ retry: false, orgId: undefined });
          } else {
            await initialize({ retry: false, orgId: selectedOrgId });
          }
        } else {
          setIsError(true);
        }
      }
    };

    if (
      isLoaded &&
      isOrganizationListLoaded &&
      !isReady &&
      !didInitialize.current &&
      !userMemberships.isLoading
    ) {
      if (signInOrg && signInOrg !== orgId) {
        void selectOrganization(signInOrg);
      } else if (orgId) {
        void initialize({
          orgId: orgId,
          retry: true,
        });
      } else {
        const firstOrganization = userMemberships.data?.[0]?.organization;

        if (firstOrganization) {
          void selectOrganization(firstOrganization.id);
        } else {
          void initialize({
            retry: true,
          });
        }
      }
    }
  }, [
    client,
    getToken,
    isLoaded,
    isOrganizationListLoaded,
    isReady,
    orgId,
    setupUser,
    user,
    userMemberships,
    selectOrganization,
    signInOrg,
    refreshToken,
  ]);

  const signOut = useCallback(async () => {
    amplitude.reset();
    posthog.reset();
    return clerkSignOut();
  }, [clerkSignOut, posthog]);

  const value: UserContextType = useMemo(() => {
    return {
      isReady,
      isError,
      user: meQueryResponse.data?.me,
      billingInfo: billingInfo ?? defaultUserContext.billingInfo,
      usageForCurrentPeriod: meQueryResponse.data?.usageForCurrentPeriod ?? {
        articles: 0,
      },
      keywordQueryUsageForCurrentPeriod: meQueryResponse.data
        ?.keywordQueryUsageForCurrentPeriod ?? {
        keywordQueries: 0,
      },
      tenant: meQueryResponse.data?.tenant,
      keywordQueryCreditsRemaining:
        (billingInfo?.keywordQueryCredits ?? 0) +
        (billingInfo?.keywordQueriesPerMonth ?? 0) -
        (meQueryResponse.data?.keywordQueryUsageForCurrentPeriod
          .keywordQueries ?? 0),
      articlesRemaining:
        (billingInfo?.credits ?? 0) +
        (billingInfo?.articlesPerMonth ?? 0) -
        (meQueryResponse.data?.usageForCurrentPeriod.articles ?? 0),
      userOrganizations: userMemberships?.data?.map((membership) => ({
        id: membership.organization.id,
      })),

      signOut,
      selectOrganization,
    };
  }, [
    isReady,
    isError,
    meQueryResponse.data?.me,
    meQueryResponse.data?.usageForCurrentPeriod,
    meQueryResponse.data?.keywordQueryUsageForCurrentPeriod,
    meQueryResponse.data?.tenant,
    billingInfo,
    userMemberships?.data,
    signOut,
    selectOrganization,
  ]);

  useEffect(() => {
    Sentry.setTag("tenantId", value.tenant?.id);
    Sentry.setTag("tenantName", value.tenant?.name);
    Sentry.setTag("billingTier", value.billingInfo?.billingTier);
    Sentry.setUser({
      id: value.user?.id,
      email: value.user?.email ?? undefined,
    });
  });

  useEffect(() => {
    if (value.user?.identityId) {
      posthog.identify(value.user.identityId);
    }

    if (value.tenant?.identityId) {
      posthog.group("tenant", value.tenant.id);
    }
  }, [posthog, value.tenant, value.user]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export default UserProvider;
